import React, { useMemo } from 'react'
import { Panel, List, Tag, FlexboxGrid } from 'rsuite'
import { AlertCircleOutline, TimeOutline, WarningOutline } from 'react-ionicons'
import { generatePath, Link } from 'react-router-dom'
import { format } from 'date-fns'

import { ROUTE_ORDERS, ROUTE_ORDERS_VIEW_ORDER, ROUTE_ORDERS_EDIT_ORDER } from '@@partner:config/routes'
import { useFetchOrders } from '@@partner:graphql/query/order/allOrders.gql'
import { isEditable } from '@@partner:helpers/order'
import { ORDER } from '@hive/status'
import type { OrderInfo } from '@@partner:types:app'

import './UrgentOrdersPanel.scss'

// Статусы активных заказов, которые нужно отслеживать
const ACTIVE_ORDER_STATUSES = [
  ORDER.CONFIRMED,
  ORDER.ASSEMBLY_IN_PROGRESS,
  ORDER.ASSEMBLY_COMPLETE,
  ORDER.WAITING_FOR_PAYMENT
]

// Временные пороги в миллисекундах
const FIVE_MIN = 5 * 60 * 1000
const FIFTEEN_MIN = 15 * 60 * 1000
const THIRTY_MIN = 30 * 60 * 1000

type UrgencyLevel = 'critical' | 'warning' | null

interface UrgentOrder {
  order: OrderInfo
  urgencyLevel: UrgencyLevel
  timeLeftMs: number
}

/**
 * Определение уровня срочности заказа
 *
 * Размещён: Желтый < 30 мин, Красный < 15 мин
 * Собирается: Желтый < 15 мин, Красный < 5 мин
 * Собран / Ожидает оплаты: Желтый < 5 мин
 */

function getOrderUrgency (order: OrderInfo): { level: UrgencyLevel; timeLeftMs: number } {
  const now = Date.now()
  const slot = order.deliverySlot

  if (!slot?.period?.from) {
    return { level: null, timeLeftMs: Infinity }
  }

  const startTime = parseInt(slot.period.from)
  const timeLeftMs = startTime - now

  switch (order.status) {
  case ORDER.CONFIRMED:
    // Размещён: Красный < 15 мин, Желтый < 30 мин
    if (timeLeftMs < FIFTEEN_MIN) return { level: 'critical', timeLeftMs }
    if (timeLeftMs < THIRTY_MIN) return { level: 'warning', timeLeftMs }
    return { level: null, timeLeftMs }

  case ORDER.ASSEMBLY_IN_PROGRESS:
    // Собирается: Красный < 5 мин, Желтый < 15 мин
    if (timeLeftMs < FIVE_MIN) return { level: 'critical', timeLeftMs }
    if (timeLeftMs < FIFTEEN_MIN) return { level: 'warning', timeLeftMs }
    return { level: null, timeLeftMs }

  case ORDER.ASSEMBLY_COMPLETE:
  case ORDER.WAITING_FOR_PAYMENT:
    // Собран / Ожидает оплаты: Желтый < 5 мин
    if (timeLeftMs < FIVE_MIN) return { level: 'warning', timeLeftMs }
    return { level: null, timeLeftMs }

  default:
    return { level: null, timeLeftMs }
  }
}

function formatTimeLeft (ms: number): string {
  if (ms < 0) {
    const absMs = Math.abs(ms)
    const minutes = Math.floor(absMs / 60000)
    if (minutes < 60) {
      return `просрочен на ${minutes} мин`
    }
    const hours = Math.floor(minutes / 60)
    return `просрочен на ${hours} ч ${minutes % 60} мин`
  }
  
  const minutes = Math.floor(ms / 60000)
  if (minutes < 60) {
    return `${minutes} мин`
  }
  const hours = Math.floor(minutes / 60)
  return `${hours} ч ${minutes % 60} мин`
}

function getStatusLabel (status: number): string {
  switch (status) {
  case ORDER.CONFIRMED:
    return 'Размещён'
  case ORDER.ASSEMBLY_IN_PROGRESS:
    return 'Собирается'
  case ORDER.ASSEMBLY_COMPLETE:
    return 'Собран'
  case ORDER.WAITING_FOR_PAYMENT:
    return 'Ожидает оплаты'
  default:
    return 'Неизвестно'
  }
}

interface UrgentOrdersPanelProps {
  brandSlug: string
}

export default function UrgentOrdersPanel ({ brandSlug }: UrgentOrdersPanelProps) {
  // Загружаем активные заказы
  const { data, loading } = useFetchOrders({
    filter: {
      fields: [ 'status' ],
      op: 'contains',
      value: ACTIVE_ORDER_STATUSES
    },
    pager: { page: 1, limit: 100 },
    pollInterval: 30000 // Обновляем каждые 30 секунд
  })

  const orders = useMemo(() => data?.orders?.edges ?? [], [ data?.orders?.edges ])

  // Фильтруем и сортируем срочные заказы
  const urgentOrders = useMemo<UrgentOrder[]>(() => {
    const result: UrgentOrder[] = []
    
    orders.forEach(order => {
      const { level, timeLeftMs } = getOrderUrgency(order)
      if (level) {
        result.push({ order, urgencyLevel: level, timeLeftMs })
      }
    })
    
    // Сортируем: сначала критические, затем по времени
    return result.sort((a, b) => {
      if (a.urgencyLevel === 'critical' && b.urgencyLevel !== 'critical') return -1
      if (a.urgencyLevel !== 'critical' && b.urgencyLevel === 'critical') return 1
      return a.timeLeftMs - b.timeLeftMs
    })
  }, [ orders ])

  const criticalCount = urgentOrders.filter(o => o.urgencyLevel === 'critical').length
  const warningCount = urgentOrders.filter(o => o.urgencyLevel === 'warning').length

  // Формируем URL для фильтра по статусам
  const ordersUrl = useMemo(() => {
    const params = new URLSearchParams()
    ACTIVE_ORDER_STATUSES.forEach((status, index) => {
      params.append(`statuses[${index}]`, String(status))
    })
    return `${generatePath(ROUTE_ORDERS, { brandSlug })}?${params.toString()}`
  }, [ brandSlug ])

  return (
    <Panel 
      header={
        <FlexboxGrid justify="space-between" align="middle">
          <FlexboxGrid.Item>
            <span>Срочные заказы</span>
            {criticalCount > 0 && (
              <Tag color="red" style={{ marginLeft: 8 }}>{criticalCount}</Tag>
            )}
            {warningCount > 0 && (
              <Tag color="orange" style={{ marginLeft: 4 }}>{warningCount}</Tag>
            )}
          </FlexboxGrid.Item>
          <Link to={ordersUrl}>
            <Tag color="blue">Все активные</Tag>
          </Link>
        </FlexboxGrid>
      } 
      bordered 
      className="UrgentOrdersPanel"
    >
      {loading && urgentOrders.length === 0 ? (
        <div className="UrgentOrdersPanel__EmptyState">Загрузка...</div>
      ) : urgentOrders.length > 0 ? (
        <List>
          {urgentOrders.slice(0, 10).map(({ order, urgencyLevel, timeLeftMs }) => {
            const isCritical = urgencyLevel === 'critical'
            const orderRoute = isEditable(order.status) ? ROUTE_ORDERS_EDIT_ORDER : ROUTE_ORDERS_VIEW_ORDER
            const orderUrl = generatePath(orderRoute, { brandSlug, orderId: order.id })
            const slotTime = order.deliverySlot?.slotTime
            
            return (
              <List.Item key={order.id} className="UrgentOrdersPanel__Item">
                <FlexboxGrid justify="space-between" align="middle">
                  <FlexboxGrid.Item>
                    <div className="UrgentOrdersPanel__Info">
                      {isCritical ? (
                        <AlertCircleOutline color="#f5222d" height="18px" width="18px" />
                      ) : (
                        <WarningOutline color="#faad14" height="18px" width="18px" />
                      )}
                      <Link to={orderUrl} className="UrgentOrdersPanel__OrderId">
                        #{order.id}
                      </Link>
                      <Tag size="sm" color={isCritical ? 'red' : 'orange'}>
                        {getStatusLabel(order.status)}
                      </Tag>
                    </div>
                    <div className="UrgentOrdersPanel__Details">
                      {slotTime && <span>Слот: {slotTime}</span>}
                      {order.shop?.name && <span> • {order.shop.name}</span>}
                    </div>
                  </FlexboxGrid.Item>
                  <FlexboxGrid.Item>
                    <div className={`UrgentOrdersPanel__TimeLeft ${isCritical ? 'UrgentOrdersPanel__TimeLeft--critical' : 'UrgentOrdersPanel__TimeLeft--warning'}`}>
                      <TimeOutline height="14px" width="14px" color={isCritical ? '#f5222d' : '#faad14'} />
                      <span>{formatTimeLeft(timeLeftMs)}</span>
                    </div>
                  </FlexboxGrid.Item>
                </FlexboxGrid>
              </List.Item>
            )
          })}
        </List>
      ) : (
        <div className="UrgentOrdersPanel__EmptyState UrgentOrdersPanel__EmptyState--success">
          <span>Нет срочных заказов</span>
        </div>
      )}
    </Panel>
  )
}
